import { computed, toRef } from "vue";

import type { PrimitiveOrArrayValue } from "@/lib/components/logic/atoms/input/props";

import { primitiveOrArrayValue } from "@/lib/components/logic/atoms/input/props";
import { propsDefinition } from "@/lib/composables/componentComposable";

type UseCharacterCounterScoped = {
  characterCounter?: boolean | number | string;
  disabled?: boolean;
  maxlength?: number;
  modelValue?: PrimitiveOrArrayValue;
};

type UseCharacterCounterProps = UseCharacterCounterScoped;

const scoped = propsDefinition({
  characterCounter: { type: [Boolean, Number, String], default: false },
  disabled: { type: Boolean, default: false },
  maxlength: { type: Number, required: false },
  modelValue: primitiveOrArrayValue,
}) satisfies Record<keyof UseCharacterCounterScoped, unknown>;

const props = scoped;

function use(props: UseCharacterCounterProps) {
  const length = computed(() => {
    if (Array.isArray(props.modelValue)) {
      return props.modelValue.length;
    }

    return props.modelValue?.toString().length || 0;
  });

  const maxReached = computed(() => {
    if (!props.maxlength) {
      return false;
    }
    return length.value >= (props.maxlength satisfies number);
  });

  const percentageOfMax = computed(
    () => (length.value / Number(props.maxlength)) * 100,
  );

  const shouldShow = computed(() => {
    if (!props.maxlength) {
      return false;
    }
    if (typeof props.characterCounter === "boolean") {
      return props.characterCounter;
    }
    return percentageOfMax.value >= Number(props.characterCounter);
  });

  return {
    length,
    maxlength: toRef(() => props.maxlength),
    maxReached,
    shouldShow,
    disabled: toRef(() => props.disabled),
  };
}

function vIf({
  maxlength,
  characterCounter,
}: Pick<UseCharacterCounterProps, "characterCounter" | "maxlength">) {
  return computed(() => !!maxlength && characterCounter !== false);
}

export type { UseCharacterCounterProps, UseCharacterCounterScoped };
export { props, scoped, use, vIf };
